import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ErrorBoundary from './components/custom-components/ErrorBoundary';
import { AuthProvider } from './contexts/AuthContext';
import { StatsProvider } from './contexts/StatsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Silk from './components/custom-components/Silk';
import SmoothScroll from './components/custom-components/SmoothScroll';
import initializeGSAP from './utils/gsapConfig';
import { preloadStargazersData } from './services/dataPreloader';
import './i18n/i18n';

const Hero = lazy(() => import('./components/Hero'));
const Why = lazy(() => import('./components/Why'));
const Stats = lazy(() => import('./components/Stats'));
const Pricing = lazy(() => import('./components/Pricing'));
const Features = lazy(() => import('./components/Features'));
const Diff = lazy(() => import('./components/Diff'));
const FAQ = lazy(() => import('./components/FAQ'));
const Contact = lazy(() => import('./components/Contact'));
const Footer = lazy(() => import('./components/Footer'));
const DocsLayout = lazy(() => import('./docs/components/DocsLayout'));

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-[#0b0d0e]">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#027DFD]"></div>
  </div>
);

const LandingPage = () => (
  <SmoothScroll>
    <div className="App">
      <Suspense fallback={<LoadingSpinner />}>
        <Hero />
        <Why />
        <Features />
        <Diff />
        <Pricing />
        <FAQ />
        <Contact />
        <Stats />
        <Footer />
      </Suspense>
    </div>
  </SmoothScroll>
);

function App() {
  useEffect(() => {
    // Initialize GSAP
    initializeGSAP();

    // Start preloading stargazers data immediately on app startup
    preloadStargazersData().catch(error => {
      console.warn('Failed to preload stargazers data:', error);
    });

    // Admin utilities removed

    // Additional ResizeObserver error suppression and chart-related errors
    const handleError = (event) => {
      if (event.message && (
        event.message.includes('ResizeObserver loop completed') ||
        event.message.includes('Cannot read properties of null (reading \'ownerDocument\')') ||
        event.message.includes('Chart resize error') ||
        event.message.includes('helpers.dom.ts')
      )) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    const handleUnhandledRejection = (event) => {
      if (event.reason && event.reason.message && (
        event.reason.message.includes('Chart resize error') ||
        event.reason.message.includes('Cannot read properties of null')
      )) {
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <StatsProvider>
              <Router>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <>
                        {/* Silk background for home page only - outside smooth scroll */}
                        <div className="fixed inset-0 -z-50">
                          <Silk
                            speed={5}
                            scale={0.5}
                            color="#000C24"
                            noiseIntensity={0.2}
                            rotation={1.3}
                          />
                        </div>
                        <LandingPage />
                      </>
                    }
                  />
                  <Route
                    path="/docs/*"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <DocsLayout />
                      </Suspense>
                    }
                  />
                </Routes>
              </Router>
            </StatsProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;